import { navigate } from "gatsby";
import React, { useState, useEffect } from "react";
import { Maybe, SanityWhereToBuyList, SanityStore } from "@graphql-types";
import { WhereToBuyListContainer, StoreCardStyle, Filters } from "./whereToBuyList.style";
import { Link, Image, Dropdown } from "@global";
import { getAllBrands } from "@api";
import { urlQueryNames, mapSlugToBrand } from "@util/constants";
import { isBrowser } from "@util/helper";

interface Props {
  data: Maybe<SanityWhereToBuyList> | undefined;
  extraData: Array<SanityStore> | undefined;
}

function WhereToBuyList({ data, extraData }: Props) {
  if (!data || !extraData) return null;

  const [selectedBrandTitle, setSelectedBrandTitle] = useState<string | null>();
  const { onlineButtonText, inStoreButtonText } = data;

  const {
    allSanityBrand: { nodes },
  } = getAllBrands();

  if (!nodes) return null;

  // Additional brands that are not part of the main brand list
  const additionalBrand = "Hug A Lumps";
  const defaultBrand = "ZURU";

  // Combine all brand titles, including additional and default brands, removing duplicates
  const brandTitles = [...new Set(nodes.map(brand => brand.title)), additionalBrand, defaultBrand];

  // Function to get the brand from the URL query parameter
  const getBrandFromUrl = () => {
    if (isBrowser()) {
      const itemUrlQuery = new URLSearchParams(window.location.search).get(urlQueryNames.brand);
      // Return the brand from URL or default to "zuru" if not found
      return itemUrlQuery || "zuru";
    }
    return "zuru"; // Default brand for non-browser environments
  };

  // Handler for brand selection change
  const handleChange = (selectedBrandTitle: string) => {
    // Find the corresponding query parameter for the selected brand title
    const queryParams = Object.keys(mapSlugToBrand).find(
      key => mapSlugToBrand[key as keyof typeof mapSlugToBrand] === selectedBrandTitle,
    );
    setSelectedBrandTitle(queryParams);

    // Update the URL with the new brand query parameter
    if (isBrowser()) {
      const newQuery = new URLSearchParams(window.location.search);
      newQuery.set(urlQueryNames.brand, queryParams);
      navigate(`?${newQuery.toString()}`, { replace: true });
    }
  };

  // Effect to set the initial selected brand based on the URL when the component loads
  useEffect(() => {
    if (isBrowser()) {
      setSelectedBrandTitle(getBrandFromUrl());
    }
  }, [isBrowser]);

  return (
    <WhereToBuyListContainer size="default">
      <Filters>
        {selectedBrandTitle && (
          <Dropdown
            title={mapSlugToBrand[selectedBrandTitle]}
            onChange={handleChange}
            data={brandTitles}
          />
        )}
      </Filters>
      <ul>
        {extraData.map(store => {
          const { storeLogo, inStoreUrl, onlineQueryUrl, _id } = store;
          const onlineQueryWithBrand =
            onlineQueryUrl &&
            selectedBrandTitle &&
            onlineQueryUrl.replace("[DYNAMIC_ITEM]", encodeURI(mapSlugToBrand[selectedBrandTitle]));

          return (
            <StoreCardStyle key={_id}>
              <div>{storeLogo && <Image data={storeLogo} />}</div>
              <div>
                <Link externalLink={inStoreUrl} linkText={inStoreButtonText} design="outlineDark" />
                {onlineQueryWithBrand && (
                  <Link
                    externalLink={onlineQueryWithBrand}
                    linkText={onlineButtonText}
                    design="outlineDark"
                  />
                )}
              </div>
            </StoreCardStyle>
          );
        })}
      </ul>
    </WhereToBuyListContainer>
  );
}

export default WhereToBuyList;
